/**
 * RBAC permission definitions.
 * Roles: VIEWER < OPERATOR < ADMIN
 *
 * Usage:
 *   const { canEdit, canAdmin } = usePermissions();
 */

export type UserRole = 'VIEWER' | 'OPERATOR' | 'ADMIN';

export interface Permissions {
  // Read permissions (all roles)
  canView: boolean;
  // Operational permissions (OPERATOR + ADMIN)
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canStartBot: boolean;
  canStopBot: boolean;
  canRestartBot: boolean;
  canHaltBot: boolean;
  canResumeBot: boolean;
  canCancelOrders: boolean;
  // Admin-only permissions
  canAdmin: boolean;
  canManageUsers: boolean;
  canSystemHalt: boolean;
  canSystemResume: boolean;
  canManageFeatureFlags: boolean;
  canViewSecurityEvents: boolean;
  canManageAuditRetention: boolean;
  canAnonymizeUsers: boolean;
  canManageKillSwitch: boolean;
  // Operational + Admin permissions
  canManageVenueRiskCaps: boolean;
  canManageExecutionProfiles: boolean;
  canManageVenueConnections: boolean;
  canManageStrategies: boolean;
  canManageWallets: boolean;
  canManageNotificationChannels: boolean;
  // Admin-only — workspace governance (LOK-1113)
  canManageApiKeys: boolean;
  canManageWorkspace: boolean;
}

const VIEWER_PERMISSIONS: Permissions = {
  canView: true,
  canCreate: false,
  canEdit: false,
  canDelete: false,
  canStartBot: false,
  canStopBot: false,
  canRestartBot: false,
  canHaltBot: false,
  canResumeBot: false,
  canCancelOrders: false,
  canAdmin: false,
  canManageUsers: false,
  canSystemHalt: false,
  canSystemResume: false,
  canManageFeatureFlags: false,
  canViewSecurityEvents: false,
  canManageAuditRetention: false,
  canAnonymizeUsers: false,
  canManageKillSwitch: false,
  canManageVenueRiskCaps: false,
  canManageExecutionProfiles: false,
  canManageVenueConnections: false,
  canManageStrategies: false,
  canManageWallets: false,
  canManageNotificationChannels: false,
  canManageApiKeys: false,
  canManageWorkspace: false,
};

const OPERATOR_PERMISSIONS: Permissions = {
  canView: true,
  canCreate: true,
  canEdit: true,
  canDelete: true,
  canStartBot: true,
  canStopBot: true,
  canRestartBot: true,
  canHaltBot: true,
  canResumeBot: true,
  canCancelOrders: true,
  canAdmin: false,
  canManageUsers: false,
  canSystemHalt: false,
  canSystemResume: false,
  canManageFeatureFlags: false,
  canViewSecurityEvents: false,
  canManageAuditRetention: false,
  canAnonymizeUsers: false,
  canManageKillSwitch: false,
  canManageVenueRiskCaps: true,
  canManageExecutionProfiles: true,
  canManageVenueConnections: true,
  canManageStrategies: true,
  canManageWallets: true,
  canManageNotificationChannels: true,
  canManageApiKeys: false,
  canManageWorkspace: false,
};

const ADMIN_PERMISSIONS: Permissions = {
  canView: true,
  canCreate: true,
  canEdit: true,
  canDelete: true,
  canStartBot: true,
  canStopBot: true,
  canRestartBot: true,
  canHaltBot: true,
  canResumeBot: true,
  canCancelOrders: true,
  canAdmin: true,
  canManageUsers: true,
  canSystemHalt: true,
  canSystemResume: true,
  canManageFeatureFlags: true,
  canViewSecurityEvents: true,
  canManageAuditRetention: true,
  canAnonymizeUsers: true,
  canManageKillSwitch: true,
  canManageVenueRiskCaps: true,
  canManageExecutionProfiles: true,
  canManageVenueConnections: true,
  canManageStrategies: true,
  canManageWallets: true,
  canManageNotificationChannels: true,
  canManageApiKeys: true,
  canManageWorkspace: true,
};

export const PERMISSION_MAP: Record<UserRole, Permissions> = {
  VIEWER: VIEWER_PERMISSIONS,
  OPERATOR: OPERATOR_PERMISSIONS,
  ADMIN: ADMIN_PERMISSIONS,
};

export function getPermissions(role: UserRole | null | undefined): Permissions {
  if (!role || !(role in PERMISSION_MAP)) return VIEWER_PERMISSIONS;
  return PERMISSION_MAP[role];
}

export function hasMinimumRole(userRole: UserRole, requiredRole: UserRole): boolean {
  const roleOrder: UserRole[] = ['VIEWER', 'OPERATOR', 'ADMIN'];
  return roleOrder.indexOf(userRole) >= roleOrder.indexOf(requiredRole);
}
